import {
  Grid,
  GridCellProps,
  GridNoRecords,
  getSelectedState,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { useCallback, useRef, useState } from 'react';
import { getter } from '@progress/kendo-react-common';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { TableSelectionChangeEvent } from '@progress/kendo-react-data-tools';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { appStore } from '../../../../stores/app-store';
import { Loader } from '@progress/kendo-react-indicators';
import { useToast } from '../../../../contexts/toast-context';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { getSelectedIds } from '../../../../utils/report/grid-helper';
import { ActionCell } from '../../../../components/grid/actions-cell';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { DownloadReportPlayerAsync } from '../../../../services/event-reports';
import { useGetEventReportGameTab } from '../../../../hooks/services/use-reports';
import { EventReportHeaderResponse } from '../../../../types/responses/event-report-response';

interface PlayerProps {
  headerReport: EventReportHeaderResponse;
}

export const PlayersReportTab = ({ headerReport }: PlayerProps) => {
  const { eventId, detailId } = useParams();

  const initialDataState: State = {
    sort: [{ field: 'name', dir: 'asc' }],
    ...DefaultGridSettings.initialDataState
  };
  const idGetter = getter('id');

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const { onToastOpen } = useToast();

  const { data, isLoading } = useGetEventReportGameTab({
    tab: 'players',
    eventId,
    playlistId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });

  const onActionRow = (props: GridCellProps) => {
    return (
      <ActionCell
        title={''}
        icon={'file_download'}
        themeColor={'primary'}
        className={'fs-2'}
        onClick={() => {
          onDownloadRowToPdf(props.dataItem.id);
        }}
      />
    );
  };

  const onDownloadRowToPdf = async (playerId: number) => {
    try {
      appStore.showLoading();
      await DownloadReportPlayerAsync(parseInt(eventId!), headerReport.name, [
        playerId
      ]);

      onToastOpen({
        title: 'Player Report',
        body: <div>Report downloaded.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Player Report',
        body: <div>Failed to download report.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onDownloadSelected = async () => {
    try {
      appStore.showLoading();
      const ids = getSelectedIds(selectedState);
      await DownloadReportPlayerAsync(
        parseInt(eventId!),
        headerReport.name,
        ids
      );

      onToastOpen({
        title: 'Player Report',
        body: <div>Report downloaded.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Player Report',
        body: <div>Failed to download report.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onSelectionChange = useCallback(
    (event: TableSelectionChangeEvent<any>) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: 'id'
      });
      setSelectedState(newSelectedState);
    },
    [selectedState]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event.syntheticEvent.currentTarget;
      const checked = checkboxElement.checked;
      const newSelectedState: any = {};

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    []
  );

  const gridExportRef = useRef<ExcelExport | null>(null);

  return (
    <div
      className={'pt-2'}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
      <GridToolBar
        searchPlaceholder={'Search player'}
        columnsToSearch={['name']}
        exportRef={gridExportRef}
        showGridMode={false}
        showCardMode={false}
        showBulkExportAsZip
        isDisablePdfButton={getSelectedIds(selectedState).length === 0}
        handleBulkExportAsZip={onDownloadSelected}
        {...dataState}
        onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}></GridToolBar>
      <ExcelExport
        data={data?.data ?? []}
        ref={gridExportRef}
        fileName={`player.xlsx`}>
        <Grid
          id={'grid-player'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          dataItemKey={'id'}
          data={data}
          select={selectedState}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'selected'}
            width="50px"
            columnType="checkbox"
            headerSelectionValue={
              (data?.data ?? []).length > 0 &&
              (data?.data ?? []).every(
                (item: any) => selectedState[idGetter(item)]
              )
            }
          />
          <Column
            field={'name'}
            title={'Player Name'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'teamName'}
            title={'Team Name'}
            filter={'text'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'groupName'}
            title={'Group Name'}
            filter={'text'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'score'}
            title={'Total Score'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'duration'}
            title={'Duration'}
            columnMenu={ColumnMenu}
          />
          <Column field={'activity'} title={'Game'} columnMenu={ColumnMenu} />
          <Column title={''} cells={{ data: onActionRow }} width={50} />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </ExcelExport>
    </div>
  );
};
